import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import SEO from '@/components/common/SEO';
import PhotoGalleryModal from '@/components/common/PhotoGalleryModal';
import userService, {
  PublicUserProfile,
  UserCommunityItem,
  type SportCategoryStatItem,
} from '@/services/userService';
import { ALL_RACKET_METRICS_LIST } from '@/pages/client/ClientRacketStatsSettingsPage';
import { ALL_METRICS_LIST } from '@/pages/client/ClientStatsSettingsPage';

const calculateAge = (birthDateStr?: string): string => {
  if (!birthDateStr) return '-';
  const birth = new Date(birthDateStr);
  if (isNaN(birth.getTime())) return '-';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age > 0 ? `${age} tahun` : '-';
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const HASIL_SPORTS = ['futsal', 'mini-soccer', 'minisoccer', 'sepak-bola', 'sepakbola', 'football', 'padel', 'badminton'];

const mapApiEventToCardEvent = (apiEvent: any) => {
  const eventDate = apiEvent.event_date ? new Date(apiEvent.event_date) : new Date();
  const dateDisplay = formatDate(apiEvent.event_date);

  const locationDisplay = apiEvent.lokasi_arena || apiEvent.address || 'Arena Sporto';
  const cityDisplay = apiEvent.city || 'Yogyakarta';
  const hostName =
    apiEvent.host?.name || apiEvent.host_name || '';
  const hostAvatar =
    apiEvent.host?.avatar ||
    apiEvent.host?.avatar_url ||
    apiEvent.host_avatar ||
    apiEvent.host_logo ||
    '';

  return {
    id: apiEvent.id,
    title: apiEvent.name,
    slug: apiEvent.slug,
    date: dateDisplay,
    location: locationDisplay,
    city: cityDisplay,
    is_sparring: apiEvent.is_sparring,
    badge: apiEvent.badge,
    badge_bg: apiEvent.badge_bg,
    categorySlug: apiEvent.sport_category_slug || apiEvent.sport_category?.slug || '',
    categoryName: apiEvent.sport_category_name || apiEvent.sport_category?.name || '',
    hostName,
    hostAvatar,
    eventDate,
  };
};

export const ClientPublicProfilePage = () => {
  const { username, slug: paramSlug } = useParams<{ username?: string; slug?: string }>();
  const slug = username || paramSlug;
  const navigate = useNavigate();

  const [user, setUser] = useState<PublicUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [communities, setCommunities] = useState<UserCommunityItem[]>([]);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState<string[]>([]);
  const [isLoadingGallery, setIsLoadingGallery] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [activeHistoryTab, setActiveHistoryTab] = useState<'mabar' | 'sparing'>('mabar');
  const [activeStatSport, setActiveStatSport] = useState<string>('minisoccer');
  const [copied, setCopied] = useState(false);

  // Gallery Preview Lightbox State
  const [galleryPreviewOpen, setGalleryPreviewOpen] = useState(false);
  const [galleryPreviewIndex, setGalleryPreviewIndex] = useState(0);

  const handlePhotoClick = (index: number) => {
    setGalleryPreviewIndex(index);
    setGalleryPreviewOpen(true);
  };

  // Fetch basic profile info
  useEffect(() => {
    const fetchProfile = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const profileRes = await userService.getPublicProfile(slug);
        if (profileRes.success && profileRes.data) {
          const u = profileRes.data;
          setUser(u);
          if (u.profile_type === 'racket') {
            const raw = u.information?.active_racket_sports;
            const activeSports = Array.isArray(raw) && raw.length > 0
              ? raw.map((s: string) => String(s).toLowerCase().trim())
              : ['badminton', 'padel', 'tenis'];
            setActiveStatSport(activeSports[0] || 'badminton');
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [slug]);

  // Fetch communities from dedicated API: GET /public/users/:slug/communities
  useEffect(() => {
    const fetchCommunities = async () => {
      if (!slug) return;
      try {
        setIsLoadingCommunities(true);
        const commRes = await userService.getUserCommunities(slug);
        if (commRes.success && commRes.data) {
          setCommunities(commRes.data);
        } else {
          setCommunities([]);
        }
      } catch (error) {
        console.error('Error fetching user communities:', error);
        setCommunities([]);
      } finally {
        setIsLoadingCommunities(false);
      }
    };

    fetchCommunities();
  }, [slug]);

  // Fetch gallery from dedicated API: GET /public/users/:slug/gallery
  useEffect(() => {
    const fetchGallery = async () => {
      if (!slug) return;
      try {
        setIsLoadingGallery(true);
        const galleryRes = await userService.getUserGallery(slug);
        if (galleryRes.success && galleryRes.data) {
          setGalleryPhotos(galleryRes.data);
        } else {
          setGalleryPhotos([]);
        }
      } catch (error) {
        console.error('Error fetching user gallery:', error);
        setGalleryPhotos([]);
      } finally {
        setIsLoadingGallery(false);
      }
    };

    fetchGallery();
  }, [slug]);

  // Fetch events from dedicated API: GET /public/users/:slug/events?type=...&limit=3
  useEffect(() => {
    const fetchHistory = async () => {
      if (!slug) return;
      try {
        setIsLoadingHistory(true);
        const sportTypeToPass = user?.profile_type === 'racket' ? 'racket-game' : 'invasion-game';
        const eventsRes = await userService.getUserEvents(
          slug,
          activeHistoryTab,
          3,
          sportTypeToPass
        );
        if (eventsRes.success && eventsRes.data) {
          const mappedEvents = (eventsRes.data || []).map(mapApiEventToCardEvent);
          setEvents(mappedEvents);
        } else {
          setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching user events:', error);
        setEvents([]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchHistory();
  }, [slug, activeHistoryTab, user?.profile_type]);

  const handleShareClick = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      showToast.success('Link profil berhasil disalin!');
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy url', err);
    }
  };

  const handleEventDetailClick = (event: any) => {
    const sportSlug = (event.categorySlug || '').toLowerCase();
    if (HASIL_SPORTS.includes(sportSlug)) {
      navigate(`/event/${event.slug}/statistik`);
    } else {
      navigate(`/event/${event.slug}`);
    }
  };

  // Active racket sports list from profile
  const activeRacketSports: string[] = useMemo(() => {
    const raw = user?.information?.active_racket_sports;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map((s: string) => String(s).toLowerCase().trim());
    }
    return ['badminton', 'padel', 'tenis'];
  }, [user]);

  // Sport category stats mapping
  const sportStatsMap = useMemo(() => {
    const map: Record<string, SportCategoryStatItem> = {};
    if (user?.stats?.sport_category_stats) {
      user.stats.sport_category_stats.forEach((item) => {
        const slugLower = (item.sport_category_slug || '').toLowerCase();
        if (slugLower.includes('futsal')) {
          map['futsal'] = item;
        } else if (slugLower.includes('mini')) {
          map['minisoccer'] = item;
        } else if (slugLower.includes('sepak') || slugLower.includes('football')) {
          map['sepakbola'] = item;
        }
      });
    }
    return map;
  }, [user]);

  // Sport category stats item
  const sportStatItem = useMemo(() => {
    if (user?.sport_stats?.[activeStatSport]) {
      return user.sport_stats[activeStatSport];
    }
    return sportStatsMap[activeStatSport];
  }, [user, sportStatsMap, activeStatSport]);

  const configuredMetricKeys: string[] = useMemo(() => {
    const statConfig = user?.information?.stat_config;
    if (user?.profile_type === 'racket') {
      return (
        statConfig?.[activeStatSport] || ['poin', 'point_loss', 'total_menang', 'total_kalah']
      );
    }
    return (
      statConfig?.[activeStatSport] ||
      statConfig?.invasion_game || ['gol', 'assist', 'save', 'foul']
    );
  }, [user, activeStatSport]);

  const displayName = user?.name || user?.username || 'Pemain Sporto';
  const usernameDisplay = user?.username ? `@${user.username}` : '@pemain';
  const avatarUrl =
    user?.avatar ||
    user?.profile_image ||
    'https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&auto=format&fit=crop&q=80';

  const positionDisplay =
    user?.profile_type === 'racket'
      ? user?.information?.racket_position || user?.information?.play_type || '-'
      : user?.information?.position || '-';
  const heightDisplay = user?.information?.height ? `${user.information.height} cm` : '-';
  const dominantFootDisplay =
    user?.profile_type === 'racket'
      ? user?.information?.dominant_hand || user?.information?.dominant_foot || '-'
      : user?.information?.dominant_foot || '-';
  const ageDisplay = calculateAge(user?.birthdate);
  const locationDisplay = user?.information?.location_name || user?.location || '-';

  const mmrProfile = user?.mmr_profile;
  const isRacket = user?.profile_type === 'racket';

  if (loading) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-3xl p-8 text-center flex flex-col items-center justify-center space-y-4 border border-[#E0E6ED] shadow-2xs">
          <Icon icon="ph:spinner-gap-bold" className="w-8 h-8 animate-spin text-[#FC480D]" />
          <span className="text-xs font-semibold text-gray-400">Memuat profil publik...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-3xl p-8 text-center flex flex-col items-center justify-center space-y-3 border border-[#E0E6ED] shadow-2xs">
          <Icon icon="ph:user-circle-dashed-bold" className="w-14 h-14 text-gray-300 mx-auto" />
          <h2 className="text-lg font-bold text-[#0E1726]">Pemain Tidak Ditemukan</h2>
          <p className="text-xs text-gray-400 max-w-xs">
            Profil pemain yang kamu cari tidak ditemukan atau belum tersedia.
          </p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-9 px-4 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-bold transition-colors cursor-pointer mt-2"
          >
            Kembali
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-5">
      <SEO
        title={`${displayName} (${usernameDisplay}) | Sporto`}
        description={user.bio || `Profil publik ${displayName} di Sporto.`}
      />

      <main className="max-w-2xl mx-auto px-4 pt-4 space-y-5 pb-24">
        {/* 1. HERO COVER & PROFILE IMAGE CARD */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs overflow-hidden">
          {/* Main Cover Photo Box */}
          <div
            className="relative w-full aspect-square md:aspect-[4/3] rounded-t-3xl bg-slate-900 bg-cover bg-center overflow-hidden flex flex-col justify-between p-4 md:p-6 text-white"
            style={{ backgroundImage: `url("${avatarUrl}")` }}
          >
            {/* Dark Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/30 pointer-events-none" />

            {/* Top Row Action Buttons */}
            <div className="relative z-10 flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-11 h-11 rounded-2xl bg-white/20 hover:bg-white/30 text-white transition-all flex items-center justify-center cursor-pointer backdrop-blur-md border border-white/20 shadow-sm"
                aria-label="Kembali"
              >
                <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
              </button>

              <button
                type="button"
                onClick={handleShareClick}
                className="h-11 px-3.5 rounded-2xl bg-white/20 hover:bg-white/30 text-white transition-all flex items-center gap-1.5 cursor-pointer backdrop-blur-md border border-white/20 shadow-sm text-xs font-semibold"
              >
                <Icon
                  icon={copied ? 'ph:check-bold' : 'ph:share-network-bold'}
                  className="w-4 h-4"
                />
                <span>{copied ? 'Tersalin' : 'Bagikan'}</span>
              </button>
            </div>

            {/* Bottom Hero Info */}
            <div className="relative z-10 space-y-2.5">
              {/* Name */}
              <div className="flex items-center gap-2">
                <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                  {displayName}
                </h1>
              </div>

              {/* Username, Position & City */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-200 font-medium">
                <span>{usernameDisplay}</span>
                {!isRacket && (
                  <>
                    <span>·</span>
                    <span>{positionDisplay}</span>
                  </>
                )}
                <span>·</span>
                <span className="flex items-center gap-1">
                  <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-300" />
                  {locationDisplay !== '-' ? locationDisplay : 'Yogyakarta'}
                </span>
              </div>
            </div>
          </div>

          {/* Stat Summary Bar (Attached below Cover) */}
          <div className="grid grid-cols-3 divide-x divide-gray-100 p-4 text-center">
            <div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
                EVENTS
              </span>
              <span className="text-xl md:text-2xl font-bold text-[#0E1726]">
                {user.stats?.events ?? user.stats?.total_events ?? 0}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
                MATCHES
              </span>
              <span className="text-xl md:text-2xl font-bold text-[#0E1726]">
                {user.stats?.matches ?? user.stats?.total_matches ?? 0}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
                WINRATE
              </span>
              <span
                className={`text-xl md:text-2xl font-bold ${
                  isRacket ? 'text-[#1D4ED8]' : 'text-[#FC480D]'
                }`}
              >
                {user.stats?.win_rate !== undefined ? `${user.stats.win_rate}%` : '0%'}
              </span>
            </div>
          </div>
        </div>

        {/* 2. MMR TIER CARD (Dark Layout #0F172A) — invasion only */}
        {mmrProfile && !isRacket && (
          <div className="bg-[#0F172A] rounded-3xl p-5 md:p-6 text-white shadow-xl space-y-4 border border-slate-800/80">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/10">
                  <Icon icon="ph:shield-bold" className="w-6 h-6 text-slate-300" />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400 block">
                    CURRENT TIER
                  </span>
                  <h2 className="text-xl font-bold text-white leading-tight">
                    {mmrProfile.tier || 'Bronze'}
                  </h2>
                </div>
              </div>

              <div className="text-right">
                <div className="flex items-baseline justify-end gap-1">
                  <span className="text-2xl md:text-3xl font-extrabold text-white">
                    {Math.round(mmrProfile.mmr ?? 0).toLocaleString('id-ID')}
                  </span>
                  <span className="text-xs font-semibold text-slate-400">MMR</span>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-1.5 pt-1">
              <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden p-0.5">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      mmrProfile.targetMMR
                        ? Math.min(100, Math.max(0, (mmrProfile.mmr / mmrProfile.targetMMR) * 100))
                        : 0
                    }%`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400 pt-0.5 font-medium">
                <span>
                  {mmrProfile.neededMMR !== undefined && mmrProfile.neededMMR !== null
                    ? `${Math.round(mmrProfile.neededMMR).toLocaleString('id-ID')} MMR ke tier berikutnya`
                    : '0 MMR ke tier berikutnya'}
                </span>
                <span className="text-slate-300">
                  {mmrProfile.nextTier || 'Silver'} ·{' '}
                  {Math.round(mmrProfile.targetMMR ?? 2000).toLocaleString('id-ID')}
                </span>
              </div>
            </div>

            <div className="border-t border-slate-800 pt-3 flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                <Icon icon="ph:trend-up-bold" className="w-4 h-4" />
                <span>
                  {(mmrProfile.delta30Days ?? 0) >= 0
                    ? `+${(mmrProfile.delta30Days ?? 0).toLocaleString('id-ID')}`
                    : (mmrProfile.delta30Days ?? 0).toLocaleString('id-ID')}{' '}
                  MMR 30 hari terakhir
                </span>
              </div>
              {mmrProfile.rankDisplay && (
                <div className="text-slate-300 font-medium">{mmrProfile.rankDisplay}</div>
              )}
            </div>
          </div>
        )}

        {/* 3. INFO PEMAIN */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          <h3 className="text-base font-bold text-[#0E1726]">Info pemain</h3>

          <div className="grid grid-cols-2 gap-3">
            {isRacket ? (
              <>
                <div className="rounded-2xl p-3.5 space-y-1">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <Icon icon="ph:users-bold" className="w-3.5 h-3.5 text-gray-400" />
                    Nomor favorit
                  </span>
                  <span className="text-sm font-bold text-[#0E1726] block">{positionDisplay}</span>
                </div>
                <div className="rounded-2xl p-3.5 space-y-1">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <Icon icon="ph:clock-bold" className="w-3.5 h-3.5 text-gray-400" />
                    Umur
                  </span>
                  <span className="text-sm font-bold text-[#0E1726] block">{ageDisplay}</span>
                </div>
                <div className="rounded-2xl p-3.5 space-y-1">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <Icon icon="ph:hand-palm-bold" className="w-3.5 h-3.5 text-gray-400" />
                    Tangan dominan
                  </span>
                  <span className="text-sm font-bold text-[#0E1726] block">{dominantFootDisplay}</span>
                </div>
                <div className="rounded-2xl p-3.5 space-y-1">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <Icon icon="ph:arrows-out-cardinal-bold" className="w-3.5 h-3.5 text-gray-400" />
                    Tinggi badan
                  </span>
                  <span className="text-sm font-bold text-[#0E1726] block">{heightDisplay}</span>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-2xl p-3.5 space-y-1">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <Icon icon="ph:map-pin" className="w-3.5 h-3.5 text-gray-400" />
                    Posisi utama
                  </span>
                  <span className="text-sm font-bold text-[#0E1726] block">{positionDisplay}</span>
                </div>
                <div className="rounded-2xl p-3.5 space-y-1">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <Icon icon="ph:arrows-out-cardinal" className="w-3.5 h-3.5 text-gray-400" />
                    Tinggi badan
                  </span>
                  <span className="text-sm font-bold text-[#0E1726] block">{heightDisplay}</span>
                </div>
                <div className="rounded-2xl p-3.5 space-y-1">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <Icon icon="ph:sneaker" className="w-3.5 h-3.5 text-gray-400" />
                    Kaki dominan
                  </span>
                  <span className="text-sm font-bold text-[#0E1726] block">{dominantFootDisplay}</span>
                </div>
                <div className="rounded-2xl p-3.5 space-y-1">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <Icon icon="ph:clock" className="w-3.5 h-3.5 text-gray-400" />
                    Umur
                  </span>
                  <span className="text-sm font-bold text-[#0E1726] block">{ageDisplay}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 3. KOMUNITAS */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-[#0E1726]">
              {isRacket ? 'Klub & komunitas' : 'Komunitas'}
            </h3>
          </div>

          {isLoadingCommunities ? (
            <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-none">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-center gap-2 shrink-0 w-16 animate-pulse">
                  <div className="w-14 h-14 rounded-full bg-gray-200" />
                  <div className="h-3 w-12 rounded-md bg-gray-200" />
                </div>
              ))}
            </div>
          ) : communities.length > 0 ? (
            <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-none">
              {communities.map((comm) => {
                const initial = comm.name ? comm.name.substring(0, 2).toUpperCase() : 'KM';
                return (
                  <div
                    key={comm.slug}
                    onClick={() => navigate(`/community/${comm.slug}`)}
                    className="flex flex-col items-center gap-1.5 shrink-0 w-16 text-center cursor-pointer group"
                  >
                    {comm.logo ? (
                      <div
                        className={`w-14 h-14 rounded-full border-2 ${
                          isRacket ? 'border-[#1D4ED8]' : 'border-[#FC480D]'
                        } overflow-hidden shadow-2xs group-hover:scale-105 transition-transform bg-gray-100`}
                      >
                        <img
                          src={comm.logo}
                          alt={comm.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div
                        className={`w-14 h-14 rounded-full border-2 ${
                          isRacket
                            ? 'border-[#1D4ED8] bg-[#ECF2FE] text-[#1D4ED8]'
                            : 'border-[#FC480D] bg-[#FFF2EE] text-[#FC480D]'
                        } flex items-center justify-center text-sm font-bold shadow-2xs group-hover:scale-105 transition-transform`}
                      >
                        {initial}
                      </div>
                    )}
                    <span className="text-[11px] font-semibold text-gray-600 leading-tight truncate w-full">
                      {comm.name}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-6 px-4 rounded-2xl border-2 border-dashed border-gray-200 text-center flex flex-col items-center justify-center space-y-3 bg-gray-50/50">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isRacket ? 'bg-[#ECF2FE] text-[#1D4ED8]' : 'bg-amber-50 text-amber-600'
                }`}
              >
                <Icon icon="ph:users-three-bold" className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-[#0E1726]">Belum bergabung ke komunitas</h4>
                <p className="text-xs text-gray-400 max-w-xs">
                  Pemain ini belum bergabung dengan komunitas manapun.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 4. RINGKASAN STATISTIK */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          <h3 className="text-base font-bold text-[#0E1726]">Ringkasan statistik</h3>

          {/* Sport Selector Pills */}
          <div
            className={`p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500 ${
              isRacket ? 'bg-[#ECF2FE]' : 'bg-[#F5F6F7]'
            }`}
          >
            {isRacket ? (
              <>
                {activeRacketSports.includes('badminton') && (
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('badminton')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'badminton'
                        ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Badminton
                  </button>
                )}
                {activeRacketSports.includes('padel') && (
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('padel')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'padel'
                        ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Padel
                  </button>
                )}
                {(activeRacketSports.includes('tenis') || activeRacketSports.includes('tennis')) && (
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('tenis')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'tenis' || activeStatSport === 'tennis'
                        ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Tenis
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setActiveStatSport('minisoccer')}
                  className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                    activeStatSport === 'minisoccer'
                      ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                      : 'hover:text-[#0E1726]'
                  }`}
                >
                  Mini soccer
                </button>
                <button
                  type="button"
                  onClick={() => setActiveStatSport('sepakbola')}
                  className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                    activeStatSport === 'sepakbola'
                      ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                      : 'hover:text-[#0E1726]'
                  }`}
                >
                  Sepak bola
                </button>
                <button
                  type="button"
                  onClick={() => setActiveStatSport('futsal')}
                  className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                    activeStatSport === 'futsal'
                      ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                      : 'hover:text-[#0E1726]'
                  }`}
                >
                  Futsal
                </button>
              </>
            )}
          </div>

          {/* 2x2 Grid Stats */}
          <div className="grid grid-cols-2 gap-3">
            {configuredMetricKeys.map((mKey) => {
              const metricList = isRacket ? ALL_RACKET_METRICS_LIST : ALL_METRICS_LIST;
              const metricDef = (metricList as any[]).find((m) => m.key === mKey) || {
                key: mKey,
                label: mKey,
                icon: 'ph:chart-bar-bold',
                bg: isRacket ? 'bg-[#ECF2FE]' : 'bg-[#FFF2EE]',
                text: isRacket ? 'text-[#1D4ED8]' : 'text-[#FC480D]',
                getValue: (s: any) => s?.[mKey] ?? 0,
              };
              const val = metricDef.getValue(sportStatItem);

              return (
                <div
                  key={mKey}
                  className="bg-white border border-[#E0E6ED] rounded-2xl p-3.5 flex items-center gap-3 shadow-2xs"
                >
                  <div
                    className={`${isRacket ? 'w-10 h-10 rounded-2xl' : 'w-9 h-9 rounded-xl'} ${metricDef.bg} ${metricDef.text} flex items-center justify-center shrink-0`}
                  >
                    <Icon icon={metricDef.icon} className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-xl font-bold text-[#0E1726] block leading-tight">{val}</span>
                    <span className="text-xs text-gray-400 font-medium leading-tight block">
                      {metricDef.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 5. RIWAYAT EVENT */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          <h3 className="text-base font-bold text-[#0E1726]">Riwayat event</h3>

          {/* History Type Tabs */}
          <div
            className={`p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500 ${
              isRacket ? 'bg-[#ECF2FE]' : 'bg-[#F5F6F7]'
            }`}
          >
            <button
              type="button"
              onClick={() => setActiveHistoryTab('mabar')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeHistoryTab === 'mabar'
                  ? isRacket
                    ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                    : 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Mabar
            </button>
            <button
              type="button"
              onClick={() => setActiveHistoryTab('sparing')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeHistoryTab === 'sparing'
                  ? isRacket
                    ? 'bg-white text-[#1D4ED8] shadow-2xs font-bold'
                    : 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Sparing
            </button>
          </div>

          <div className="divide-y divide-gray-100 space-y-2">
            {isLoadingHistory ? (
              <div className="space-y-3 pt-1">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="pt-2 flex items-center justify-between gap-3 animate-pulse">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-2xl bg-gray-200 shrink-0" />
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="h-4 w-32 bg-gray-200 rounded" />
                        <div className="h-3 w-44 bg-gray-200 rounded" />
                      </div>
                    </div>
                    <div className="h-8 w-16 bg-gray-200 rounded-full shrink-0" />
                  </div>
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400 font-medium">
                Belum ada riwayat event {activeHistoryTab === 'sparing' ? 'sparing' : 'mabar'}.
              </div>
            ) : (
              events.map((item) => {
                const subtitle = [
                  item.location,
                  item.city !== item.location ? item.city : '',
                  item.date,
                ]
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <div key={item.id} className="pt-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {item.hostAvatar ? (
                        <img
                          src={item.hostAvatar}
                          alt={item.hostName || item.title}
                          className="w-10 h-10 rounded-2xl object-cover shrink-0 border border-gray-100 shadow-2xs"
                        />
                      ) : (
                        <div
                          className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-xs shrink-0 ${
                            item.badge_bg ||
                            (isRacket ? 'bg-[#1D4ED8] text-white' : 'bg-slate-900 text-white')
                          }`}
                        >
                          {item.hostName
                            ? item.hostName.charAt(0).toUpperCase()
                            : item.badge || (item.is_sparring ? 'SP' : 'MB')}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h4 className="text-xs font-bold text-[#0E1726] truncate">{item.title}</h4>
                        <p className="text-[11px] text-gray-400 truncate mt-0.5">{subtitle}</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleEventDetailClick(item)}
                      className={`h-8 px-3 rounded-full text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer shrink-0 ${
                        isRacket
                          ? 'bg-[#ECF2FE] hover:bg-[#DBE6FE] text-[#1D4ED8]'
                          : 'bg-amber-50 hover:bg-amber-100 text-amber-600'
                      }`}
                    >
                      <span>Detail</span>
                      <Icon icon="ph:caret-right-bold" className="w-3 h-3" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* 6. GALERI */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          <h3 className="text-base font-bold text-[#0E1726]">Galeri</h3>

          {isLoadingGallery ? (
            <div className="grid grid-cols-3 gap-2.5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-square rounded-2xl bg-gray-200 animate-pulse" />
              ))}
            </div>
          ) : galleryPhotos.length > 0 ? (
            <div className="grid grid-cols-3 gap-2.5">
              {galleryPhotos.map((photo: string, idx: number) => (
                <div
                  key={idx}
                  onClick={() => handlePhotoClick(idx)}
                  className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 hover:opacity-90 transition-opacity cursor-pointer"
                >
                  <img
                    src={photo}
                    alt={`Galeri ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 px-4 rounded-2xl border-2 border-dashed border-gray-200 text-center flex flex-col items-center justify-center space-y-3 bg-gray-50/50">
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  isRacket ? 'bg-[#ECF2FE] text-[#1D4ED8]' : 'bg-orange-50 text-[#FC480D]'
                }`}
              >
                <Icon icon="ph:image-square-bold" className="w-7 h-7" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-[#0E1726]">Belum ada foto galeri</h4>
                <p className="text-xs text-gray-400 max-w-xs">
                  Pemain ini belum menambahkan foto ke galeri profilnya.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <PhotoGalleryModal
        open={galleryPreviewOpen}
        onOpenChange={setGalleryPreviewOpen}
        photos={galleryPhotos}
        initialIndex={galleryPreviewIndex}
        title={`Galeri ${displayName}`}
      />
    </div>
  );
};

export default ClientPublicProfilePage;
